import { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { BarChart3, Box, MessageSquare, Settings, Phone, LogOut, Menu, X, Calendar, ChevronRight, Home, Award } from 'lucide-react';
import { auth } from '../../lib/firebase';
import { signOut } from 'firebase/auth';

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Check authentication on component mount
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) {
        // If not authenticated, redirect to login page
        navigate('/admin/login', { replace: true });
      } else {
        // If authenticated, check if user is admin (optional)
        setIsLoading(false);
      }
    });
    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [navigate]);

  const menuItems = [
    { name: '대시보드', icon: BarChart3, path: '/admin' },
    { name: '포트폴리오 관리', icon: Box, path: '/admin/portfolios' },
    { name: '이벤트 관리', icon: Calendar, path: '/admin/events' },
    { name: '리뷰 관리', icon: MessageSquare, path: '/admin/reviews' },
    { name: '고객문의 관리', icon: Phone, path: '/admin/contacts' },
    { name: '연혁 관리', icon: Award, path: '/admin/history' },
    { name: '설정', icon: Settings, path: '/admin/settings' }
  ];

  const isActive = (path) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname === path;
  };

  const getCurrentPageName = () => {
    const currentItem = menuItems.find(item => isActive(item.path));
    return currentItem?.name || '대시보드';
  };

  const getBreadcrumbs = () => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const breadcrumbs = [{ name: '홈', path: '/', icon: Home }];
    
    if (pathSegments.length > 0 && pathSegments[0] === 'admin') {
      breadcrumbs.push({ name: '관리자', path: '/admin', icon: BarChart3 });
      if (pathSegments.length > 1) {
        const currentItem = menuItems.find(item => item.path === location.pathname);
        if (currentItem) {
          breadcrumbs.push({ name: currentItem.name, path: currentItem.path, icon: currentItem.icon });
        }
      }
    }
    return breadcrumbs;
  };

  const handleLogout = async () => {
    if (window.confirm('로그아웃 하시겠습니까?')) {
      try {
        await signOut(auth);
        navigate('/admin/login');
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">인증 확인 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-100">
      {/* 데스크톱 사이드바 */}
      <div className="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0">
        <div className="flex flex-col flex-grow bg-white shadow-lg">
          <div className="h-16 flex items-center px-6 border-b">
            <h1 className="text-xl font-bold text-gray-900">관리자</h1>
          </div>
          <nav className="mt-6 flex flex-col flex-grow">
            <div className="flex-1">
              {menuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center px-6 py-3 text-sm font-medium transition-colors ${
                      isActive(item.path)
                        ? 'bg-blue-50 text-blue-600 border-r-2 border-blue-600'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <Icon className="w-5 h-5 mr-3" />
                    {item.name}
                  </Link>
                );
              })}
            </div>
            <div className="p-4 border-t">
              <button
                onClick={handleLogout}
                className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
              >
                <LogOut className="w-5 h-5 mr-3" />
                로그아웃
              </button>
            </div>
          </nav>
        </div>
      </div>

      {/* 모바일 메뉴 오버레이 */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 flex z-40 lg:hidden">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={closeMobileMenu}></div>
          <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
            <div className="absolute top-0 right-0 -mr-12 pt-2">
              <button
                onClick={closeMobileMenu}
                className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              >
                <X className="h-6 w-6 text-white" />
              </button>
            </div>
            <div className="h-16 flex items-center px-6 border-b">
              <h1 className="text-xl font-bold text-gray-900">관리자</h1>
            </div>
            <nav className="mt-6 flex flex-col flex-grow">
              <div className="flex-1">
                {menuItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={closeMobileMenu}
                      className={`flex items-center px-6 py-3 text-sm font-medium transition-colors ${
                        isActive(item.path)
                          ? 'bg-blue-50 text-blue-600 border-r-2 border-blue-600'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <Icon className="w-5 h-5 mr-3" />
                      {item.name}
                    </Link>
                  );
                })}
              </div>
              <div className="p-4 border-t">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
                >
                  <LogOut className="w-5 h-5 mr-3" />
                  로그아웃
                </button>
              </div>
            </nav>
          </div>
        </div>
      )}

      {/* 메인 콘텐츠 */}
      <div className="lg:pl-64 flex flex-col flex-1">
        {/* 상단 헤더 */}
        <div className="sticky top-0 z-10 bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            <div className="flex items-center">
              <button
                onClick={() => setIsMobileMenuOpen(true)}
                className="lg:hidden -ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
              >
                <Menu className="h-6 w-6" />
              </button>
              {/* 브레드크럼 */}
              <nav className="flex items-center space-x-2 text-sm text-gray-500 ml-4 lg:ml-0">
                {getBreadcrumbs().map((crumb, index) => {
                  const Icon = crumb.icon;
                  const isLast = index === getBreadcrumbs().length - 1;
                  return (
                    <div key={crumb.path} className="flex items-center">
                      {index > 0 && <ChevronRight className="h-4 w-4 mx-2 text-gray-400" />}
                      <Link
                        to={crumb.path}
                        className={`flex items-center hover:text-gray-700 ${
                          isLast ? 'text-gray-900 font-medium' : 'text-gray-500'
                        }`}
                      >
                        <Icon className="h-4 w-4 mr-1" />
                        {crumb.name}
                      </Link>
                    </div>
                  );
                })}
              </nav>
            </div>
            {/* 현재 페이지 제목 (모바일) */}
            <div className="lg:hidden">
              <h1 className="text-lg font-semibold text-gray-900">{getCurrentPageName()}</h1>
            </div>
          </div>
        </div>

        {/* 페이지 콘텐츠 */}
        <main className="h-[calc(100vh-4rem)]">
          <div className="p-4 sm:p-6 lg:p-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
